import React, { useState, useEffect, useCallback } from 'react';
import '../styles/MembersArea.css';
import Navbar from './Navbar';
import UncensoredChat from './UncensoredChat';
import ImageGenerator from './ImageGenerator';
import VoiceGenerator from './VoiceGenerator';
import MusicGenerator from './MusicGenerator';
import Academy from './Academy';
import { FaMusic, FaMicrophone, FaMagic, FaHome, FaGraduationCap, FaComments } from 'react-icons/fa';
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';

const SHU_TOKEN_ADDRESS = '0x2d3a62162fa581f4d972b31fcc28ac9b2df3191b';
const SHU_TOKEN_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

const formatNumber = (value, isPrice = false) => {
  if (!value) return '$0.00';
  
  const num = parseFloat(value);
  
  // For price values (very small numbers)
  if (isPrice) {
    if (num < 0.00001) {
      return '$' + num.toFixed(8);
    }
    if (num < 0.01) {
      return '$' + num.toFixed(6);
    }
    return '$' + num.toFixed(4);
  }
  
  // For regular numbers (volume, liquidity, etc)
  if (num >= 1000000) {
    return '$' + (num / 1000000).toFixed(2) + 'M';
  }
  if (num >= 1000) {
    return '$' + (num / 1000).toFixed(2) + 'K';
  }
  
  return '$' + num.toFixed(2);
};

const formatBalance = (value) => {
  if (!value) return '0';
  const num = parseFloat(value);
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K';
  }
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const MembersArea = () => {
  const navigate = useNavigate();
  const [dexData, setDexData] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [account, setAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [shuBalance, setShuBalance] = useState(null);

  const fetchShuBalance = useCallback(async () => {
    try {
      if (!window.ethereum) return;
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = await provider.getSigner();
      const shuContract = new ethers.Contract(SHU_TOKEN_ADDRESS, SHU_TOKEN_ABI, signer);
      const decimals = await shuContract.decimals();
      const balance = await shuContract.balanceOf(account);
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      setShuBalance(formattedBalance);
    } catch (error) {
      console.error('Error fetching SHU balance:', error);
      setShuBalance(null);
    }
  }, [account]);

  useEffect(() => {
    // Check if already connected
    checkConnection();
  }, []);

  useEffect(() => {
    if (account) {
      fetchShuBalance();
    }
  }, [account, fetchShuBalance]);

  const checkConnection = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    }
  };

  const connectMetaMask = async () => {
    if (!window.ethereum) {
      window.open('https://metamask.io/download/', '_blank');
      return;
    }

    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setAccount(accounts[0]);
    } catch (error) {
      console.error('Error connecting to MetaMask:', error);
    }
    setIsConnecting(false);
  };

  const connectTrustWallet = async () => {
    if (!window.ethereum && !window.trustwallet) {
      window.open('https://trustwallet.com/download', '_blank');
      return;
    }

    setIsConnecting(true);
    try {
      const provider = window.trustwallet || window.ethereum;
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      setAccount(accounts[0]);
    } catch (error) {
      console.error('Error connecting to Trust Wallet:', error);
    }
    setIsConnecting(false);
  };

  const disconnectWallet = () => {
    setAccount(null);
  };

  // Fetch DEX data
  useEffect(() => {
    const fetchDexData = async () => {
      try {
        const response = await fetch('https://api.dexscreener.com/latest/dex/pairs/ethereum/0xe0aa3645fb283a76d739f16c03233094a71e5731');
        const data = await response.json();
        setDexData(data.pairs[0]);
      } catch (error) {
        console.error('Error fetching DEX data:', error);
      }
    };

    fetchDexData();
    const interval = setInterval(fetchDexData, 30000);
    return () => clearInterval(interval);
  }, []);

  const renderDexStats = () => {
    if (!dexData) return null;

    return (
      <div className="dex-stats">
        <div className="dex-stats-scroll">
          {shuBalance !== null && (
            <div className="stat-card">
              <h3>SHU Balance</h3>
              <div className="value">{formatBalance(shuBalance)}</div>
              <div className="sub-value">SHU Tokens</div>
              <div className="sub-value highlight">
                {formatNumber(parseFloat(shuBalance) * parseFloat(dexData.priceUsd))}
              </div>
            </div>
          )}

          <div className="stat-card">
            <h3>Price USD</h3>
            <div className="value">{formatNumber(dexData.priceUsd, true)}</div>
            <div className={`change ${parseFloat(dexData.priceChange.h24) >= 0 ? 'positive' : 'negative'}`}>
              {parseFloat(dexData.priceChange.h24).toFixed(2)}% (24h)
            </div>
            <div className="sub-value">
              {parseFloat(dexData.priceChange.h1 || 0).toFixed(2)}% (1h)
            </div>
          </div>

          <div className="stat-card">
            <h3>Liquidity</h3>
            <div className="value">{formatNumber(dexData.liquidity.usd)}</div>
            <div className="sub-value highlight">{parseFloat(dexData.liquidity.quote).toFixed(2)} FP</div>
            <div className="sub-value">{parseFloat(dexData.liquidity.base).toFixed(2)} {dexData.baseToken.symbol}</div>
          </div>

          <div className="stat-card">
            <h3>Volume (24h)</h3>
            <div className="value">{formatNumber(dexData.volume.h24)}</div>
            <div className="sub-value highlight">
              {dexData.txns.h24.buys} buys, {dexData.txns.h24.sells} sells
            </div>
            <div className="sub-value">
              {((dexData.volume.h24 / dexData.liquidity.usd) * 100).toFixed(1)}% of liquidity
            </div>
          </div>

          <div className="stat-card">
            <h3>Market Cap</h3>
            <div className="value">{formatNumber(dexData.fdv)}</div>
            <div className="sub-value highlight">Fully Diluted</div>
            <div className="sub-value">
              Supply: {parseInt(dexData.fdv / dexData.priceUsd).toLocaleString()} FP
            </div>
          </div>

          <div className="stat-card">
            <h3>24h Price Range</h3>
            <div className="value">
              {formatNumber(dexData.priceUsdHigh24h || dexData.priceUsd, true)}
            </div>
            <div className="sub-value">
              Low: {formatNumber(dexData.priceUsdLow24h || dexData.priceUsd, true)}
            </div>
            <div className="sub-value highlight">
              {parseFloat(dexData.priceChange.h24).toFixed(2)}% (24h)
            </div>
          </div>

          <div className="stat-card">
            <h3>Pair Info</h3>
            <div className="value">{dexData.baseToken.symbol}/{dexData.quoteToken.symbol}</div>
            <div className="sub-value highlight">
              {dexData.dexId.toUpperCase()} - {dexData.chainId}
            </div>
            <div className="sub-value">
              {new Date(dexData.pairCreatedAt).toLocaleDateString()}
            </div>
          </div>

          <div className="stat-card">
            <h3>6h Volume</h3>
            <div className="value">{formatNumber(dexData.volume.h6 || 0)}</div>
            <div className="sub-value highlight">Last 6 hours</div>
            <div className="sub-value">
              {((dexData.volume.h6 / dexData.volume.h24) * 100).toFixed(1)}% of 24h
            </div>
          </div>

          <div className="stat-card">
            <h3>Price Impact (1,000 SHU)</h3>
            <div className="value">{formatNumber(parseFloat(dexData.priceUsd || 0) * 1000)}</div>
            <div className="sub-value highlight">Buy Amount</div>
            <div className="sub-value">
              {((dexData.liquidity.usd / (dexData.volume.h24 || 1)) * 24).toFixed(1)}h to drain
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleToolClick = (toolId) => {
    if (toolId === 'video-studio') {
      navigate('/video-generator');
    } else {
      setSelectedTool(toolId);
    }
  };

  const menuItems = [
    { id: 'home', icon: <FaHome />, label: 'Home' },
    { id: 'text-to-image', icon: <FaMagic />, label: 'Create' },
    { id: 'ai-voice', icon: <FaMicrophone />, label: 'Voice' },
    { id: 'uncensored-chat', icon: <FaComments />, label: 'Chat' },
    { id: 'music', icon: <FaMusic />, label: 'Music' },
    { id: 'academy', icon: <FaGraduationCap />, label: 'Learn' }
  ];

  const renderAuth = () => {
    if (account) {
      return (
        <div className="auth-container connected">
          <div className="account-info">
            <div className="account-address">
              {account.slice(0, 6)}...{account.slice(-4)}
            </div>
            <button className="disconnect-button" onClick={disconnectWallet}>
              Disconnect
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-container">
        <div className="auth-content">
          <h2>Connect Wallet</h2>
          <p>Connect your wallet to access the members area</p>
          <div className="wallet-buttons">
            <button 
              className="wallet-button metamask"
              onClick={connectMetaMask}
              disabled={isConnecting}
            >
              <img src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg" alt="MetaMask" />
              <span>{isConnecting ? 'Connecting...' : 'MetaMask'}</span>
            </button>
            <button 
              className="wallet-button trustwallet"
              onClick={connectTrustWallet}
              disabled={isConnecting}
            >
              <img src="https://trustwallet.com/assets/images/media/assets/trust_platform.svg" alt="Trust Wallet" />
              <span>{isConnecting ? 'Connecting...' : 'Trust Wallet'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSelectedTool = () => {
    if (!account) {
      return renderAuth();
    }

    switch (selectedTool) {
      case 'home':
        return renderDexStats();
      case 'text-to-image':
        return <ImageGenerator />;
      case 'ai-voice':
        return <VoiceGenerator />;
      case 'uncensored-chat':
        return <UncensoredChat />;
      case 'music':
        return <MusicGenerator />;
      case 'academy':
        return <Academy />;
      default:
        return renderDexStats();
    }
  };

  return (
    <div className="members-wrapper">
      <Navbar />
      <div className="members-page">
        <div className="members-area">
          <div className="content-container">
            {renderSelectedTool()}
          </div>
          
          {account && (
            <div className="bottom-menu">
              <div className="menu-grid">
                {menuItems.map(item => (
                  <div
                    key={item.id}
                    className={`menu-item ${selectedTool === item.id ? 'active' : ''}`}
                    onClick={() => handleToolClick(item.id)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembersArea; 